import { useCallback, useEffect, useState } from "react";

/**
 * Tipo do evento `beforeinstallprompt` (não está no lib.dom.d.ts).
 * Disparado por Chrome/Edge/Android quando a PWA é elegível para instalação.
 */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

export type PwaPlatform = "ios" | "android-chrome" | "desktop" | "unknown";

function detectPlatform(): PwaPlatform {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  // iOS (Safari/Chrome/Firefox no iPad/iPhone — todos usam WebKit, mesmo prompt manual)
  if (/iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream) {
    return "ios";
  }
  // Android — geralmente Chrome / Samsung Internet
  if (/Android/.test(ua)) return "android-chrome";
  // Desktop Chromium-based (Chrome, Edge, Brave, Opera)
  return "desktop";
}

function getStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari adiciona navigator.standalone quando instalado
  const navStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  // Android/desktop usam media query display-mode:standalone
  const mediaStandalone = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  return navStandalone || mediaStandalone;
}

/**
 * Cache em nível de módulo: o `beforeinstallprompt` é disparado uma única vez,
 * normalmente logo após o load. Guardar o evento aqui permite que componentes
 * montados mais tarde (ex.: a home após navegar e voltar) ainda enxerguem o
 * prompt nativo disponível.
 */
let cachedPrompt: BeforeInstallPromptEvent | null = null;

export interface UsePwaInstall {
  platform: PwaPlatform;
  isIos: boolean;
  isStandalone: boolean;
  /** Android/desktop Chromium com prompt nativo capturado e pronto para disparar. */
  canInstallNatively: boolean;
  /** Dispara o prompt nativo. Retorna "unavailable" quando não há evento capturado. */
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
}

/**
 * Hook compartilhado de instalação PWA. Centraliza detecção de plataforma,
 * estado standalone e o evento `beforeinstallprompt` para ser consumido tanto
 * pelo card flutuante (`InstallPrompt`) quanto pelo hero embutido na home.
 */
export function usePwaInstall(): UsePwaInstall {
  const [platform] = useState<PwaPlatform>(() => detectPlatform());
  const [isStandalone, setIsStandalone] = useState<boolean>(() => getStandalone());
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    () => cachedPrompt
  );

  useEffect(() => {
    if (isStandalone) return;

    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      cachedPrompt = event as BeforeInstallPromptEvent;
      setDeferredPrompt(cachedPrompt);
    };
    const onInstalled = () => {
      cachedPrompt = null;
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [isStandalone]);

  const promptInstall = useCallback(async () => {
    const prompt = deferredPrompt ?? cachedPrompt;
    if (!prompt) return "unavailable" as const;
    await prompt.prompt();
    const choice = await prompt.userChoice;
    cachedPrompt = null;
    setDeferredPrompt(null);
    return choice.outcome;
  }, [deferredPrompt]);

  const isIos = platform === "ios";
  const canInstallNatively = !isIos && deferredPrompt !== null;

  return { platform, isIos, isStandalone, canInstallNatively, promptInstall };
}
